import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { DEPARTMENTS_SEED } from '@common/data';
import type { Department, DepartmentDeleteCheck, DepartmentDraft } from '@common/types';

import { ServiceTicketStatusesService } from './service-ticket-statuses.service';
import { ServiceTicketsService } from './service-tickets.service';

const LIST_LATENCY_MS = 200;
const SAVE_LATENCY_MS = 300;

@Injectable({ providedIn: 'root' })
export class DepartmentsService {
  private readonly statuses = inject(ServiceTicketStatusesService);
  private readonly tickets = inject(ServiceTicketsService);

  private readonly departments = signal<Department[]>(DEPARTMENTS_SEED.slice());
  private seq = 0;

  readonly all: Signal<readonly Department[]> = this.departments.asReadonly();

  readonly enabled: Signal<readonly Department[]> = computed(() =>
    this.departments().filter((d) => d.isEnabled),
  );

  list(): Observable<readonly Department[]> {
    return of(this.departments().slice()).pipe(delay(LIST_LATENCY_MS));
  }

  listEnabled(): Observable<readonly Department[]> {
    return of(this.enabled().slice()).pipe(delay(LIST_LATENCY_MS));
  }

  getById(id: string): Department | undefined {
    return this.departments().find((d) => d.id === id);
  }

  create(draft: DepartmentDraft): Observable<Department> {
    const normalizedName = draft.name.trim();
    if (this.nameTaken(normalizedName, null)) {
      return throwError(() => new Error(`A department named "${normalizedName}" already exists.`));
    }
    const created: Department = {
      id: this.generateId(normalizedName),
      name: normalizedName,
      icon: draft.icon.trim() || 'category',
      tone: draft.tone,
      description: draft.description.trim(),
      isEnabled: true,
      isSeeded: false,
    };
    this.departments.update((rows) => [...rows, created]);
    return of(created).pipe(delay(SAVE_LATENCY_MS));
  }

  update(id: string, patch: DepartmentDraft): Observable<Department> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Department not found.'));
    if (current.isSeeded) {
      return throwError(() => new Error('Default departments cannot be edited.'));
    }
    const normalizedName = patch.name.trim();
    if (this.nameTaken(normalizedName, id)) {
      return throwError(() => new Error(`A department named "${normalizedName}" already exists.`));
    }
    const next: Department = {
      ...current,
      name: normalizedName,
      icon: patch.icon.trim() || current.icon,
      tone: patch.tone,
      description: patch.description.trim(),
    };
    this.departments.update((rows) => rows.map((d) => (d.id === id ? next : d)));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  setEnabled(id: string, enabled: boolean): Observable<Department> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Department not found.'));
    const next: Department = { ...current, isEnabled: enabled };
    this.departments.update((rows) => rows.map((d) => (d.id === id ? next : d)));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  deleteCheck(id: string): DepartmentDeleteCheck {
    const open = this.tickets
      .all()
      .filter((t) => t.departmentId === id && !this.statuses.isTerminal(t.status)).length;
    return { canDelete: open === 0, openTicketCount: open };
  }

  delete(id: string): Observable<void> {
    const current = this.getById(id);
    if (!current) return throwError(() => new Error('Department not found.'));
    if (current.isSeeded) {
      return throwError(
        () => new Error('Default departments cannot be deleted — disable instead.'),
      );
    }
    const check = this.deleteCheck(id);
    if (!check.canDelete) {
      return throwError(
        () =>
          new Error(
            `Cannot delete: ${check.openTicketCount} open ticket(s) still reference this department.`,
          ),
      );
    }
    this.departments.update((rows) => rows.filter((d) => d.id !== id));
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  ticketCounts(): ReadonlyMap<string, { total: number; open: number }> {
    const map = new Map<string, { total: number; open: number }>();
    for (const t of this.tickets.all()) {
      if (!t.departmentId) continue;
      const entry = map.get(t.departmentId) ?? { total: 0, open: 0 };
      entry.total += 1;
      if (!this.statuses.isTerminal(t.status)) entry.open += 1;
      map.set(t.departmentId, entry);
    }
    return map;
  }

  private nameTaken(name: string, excludeId: string | null): boolean {
    const lowered = name.toLowerCase();
    return this.departments().some((d) => d.id !== excludeId && d.name.toLowerCase() === lowered);
  }

  private generateId(name: string): string {
    const slug =
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 32) || 'custom';
    this.seq += 1;
    return `dept-custom-${slug}-${Date.now().toString(36)}-${this.seq}`;
  }
}
